// Čista logika stranice "Korisnici dostavne firme" (bez Vue-a i bez mreže), da se može provjeriti u Node-u.
// Backend vraća korisnika sa `pivot` (po firmi) i `detail` (po osobi, može biti null); ovdje se spljošti u jedan red.

export const COURIER_TYPE = "dostava";
export const DISPATCHER_TYPE = "admin_delivery";
/** delivery_company_user.paying_type (kurir): 1 mjesečno, 2 procenat, 3 po dostavi (forma ne nudi 0 i 4, vidi N4) */
export const PAYING_TYPES = [1, 2, 3];
export const PASSWORD_MIN = 4;

/** formski ključ -> polje u tijelu zahtjeva (flat, 28.10.2026) */
export const BODY_FIELDS = {
  bank: "bank_account",
  ptype: "paying_type",
  paying: "paying",
  note: "note",
  signed: "contract_signed_at",
  from: "contract_active_from",
  dob: "date_of_birth",
  iban: "iban",
  ecn: "emergency_contact_name",
  ecp: "emergency_contact_phone",
  rurl: "referral_url",
  rshort: "referral_short_url",
  rby: "referred_by",
};
const COURIER_ONLY = Object.keys(BODY_FIELDS);

const text = (value) => (value === null || value === undefined ? "" : String(value));
const orNull = (value) => (value === "" || value === undefined ? null : value);

export const parseAmount = (value) => {
  const normalized = text(value).trim().replace(/\s/g, "").replace(",", ".");
  if (normalized === "") {
    return null;
  }
  const number = Number(normalized);
  return Number.isFinite(number) ? number : NaN;
};

/** GET /companies/{id}/users-delivery vraća { data: { ...firma, users }, status } ili samu firmu. */
export const unwrapCompany = (body) => (body && body.data && body.users === undefined ? body.data : body) || {};

export const mapUser = (raw) => {
  const pivot = raw.pivot || {};
  const detail = raw.detail || {};
  const paying = parseAmount(pivot.paying);
  return {
    id: raw.id,
    name: text(raw.name),
    lastname: text(raw.lastname),
    email: text(raw.email),
    phone: text(raw.phone),
    type: raw.type,
    imagePath: raw.image_path || "",
    createdAt: raw.created_at || "",
    qrCode: raw.qrCode || "",
    active: pivot.active === undefined || pivot.active === null ? Boolean(raw.state) : Boolean(Number(pivot.active)),
    suspended: Boolean(Number(pivot.suspended)),
    reason: text(pivot.suspended_reason),
    bank: text(pivot.bank_account),
    ptype: pivot.paying_type === null || pivot.paying_type === undefined ? null : Number(pivot.paying_type),
    paying: Number.isNaN(paying) ? null : paying,
    note: text(pivot.note),
    signed: text(pivot.contract_signed_at),
    from: text(pivot.contract_active_from),
    dob: text(detail.date_of_birth),
    iban: text(detail.iban),
    ecn: text(detail.emergency_contact_name),
    ecp: text(detail.emergency_contact_phone),
    rurl: text(detail.referral_url),
    rshort: text(detail.referral_short_url),
    rby: text(detail.referred_by),
    raw,
  };
};

export const isCourier = (user) => user.type === COURIER_TYPE;
export const statusOf = (user) => (user.suspended ? "suspended" : user.active ? "active" : "inactive");
/** Kurir bez načina plaćanja: bez toga se zarada ne može obračunati (odluka D6). */
export const isIncomplete = (user) => isCourier(user) && !user.ptype;

/** šta još nedostaje kuriru (ključevi prevoda `delivery_team.missing.*`) */
export const missingFields = (user) => {
  if (!isCourier(user)) {
    return [];
  }
  const missing = [];
  if (!user.ptype) missing.push("paying");
  if (!user.bank) missing.push("bank");
  if (!user.from) missing.push("contract");
  if (!user.phone) missing.push("phone");
  return missing;
};

export const initials = (user) => `${text(user.name).charAt(0)}${text(user.lastname).charAt(0)}`.toUpperCase();

export const countUsers = (users) => {
  const couriers = users.filter(isCourier).length;
  return {
    all: users.length,
    couriers,
    dispatchers: users.length - couriers,
    suspended: users.filter((user) => user.suspended).length,
    incomplete: users.filter(isIncomplete).length,
  };
};

/** kind: all | dostava | admin_delivery; status: all | active | inactive | suspended | incomplete */
export const filterUsers = (users, { kind = "all", status = "all", query = "" } = {}, toLatin = (s) => s) => {
  const needle = toLatin(text(query)).trim().toLowerCase();
  return users.filter((user) => {
    if (kind !== "all" && user.type !== kind) return false;
    if (status === "incomplete") {
      if (!isIncomplete(user)) return false;
    } else if (status !== "all" && statusOf(user) !== status) {
      return false;
    }
    if (needle) {
      const hay = toLatin(`${user.name} ${user.lastname} ${user.email} ${user.phone} ${user.id}`).toLowerCase();
      if (!hay.includes(needle)) return false;
    }
    return true;
  });
};

// ------------------------------------------------------------------ forma
export const emptyForm = () => ({
  id: 0,
  type: COURIER_TYPE,
  name: "",
  lastname: "",
  email: "",
  phone: "",
  password: "",
  bank: "",
  ptype: "",
  paying: "",
  note: "",
  signed: "",
  from: "",
  dob: "",
  iban: "",
  ecn: "",
  ecp: "",
  rurl: "",
  rshort: "",
  rby: "",
});

const payingToForm = (paying) => (paying === null || paying === undefined ? "" : String(paying).replace(".", ","));

export const formFromUser = (user) => ({
  id: user.id,
  type: user.type,
  name: user.name,
  lastname: user.lastname,
  email: user.email,
  phone: user.phone,
  password: "",
  bank: user.bank,
  ptype: user.ptype ? String(user.ptype) : "",
  paying: payingToForm(user.paying),
  note: user.note,
  signed: user.signed,
  from: user.from,
  dob: user.dob,
  iban: user.iban,
  ecn: user.ecn,
  ecp: user.ecp,
  rurl: user.rurl,
  rshort: user.rshort,
  rby: user.rby,
});

/** Provjera prije slanja: samo ono što backend sigurno traži; ostala pravila (jedinstven link, postojeći korisnik...) ostaju backendu. */
export const validateForm = (form, { isNew, isAdmin = false } = {}) => {
  const errors = {};
  if (!form.name.trim()) errors.name = "required";
  if (!form.lastname.trim()) errors.lastname = "required";
  if (isNew && !form.email.trim()) errors.email = "required";
  if (isNew && form.password.length < PASSWORD_MIN) errors.password = "password";
  if (!isNew && isAdmin && form.password && form.password.length < PASSWORD_MIN) errors.password = "password";
  if (isCourier(form) && form.paying !== "" && Number.isNaN(parseAmount(form.paying))) errors.paying = "number";
  return errors;
};

/** vrijednost polja iz forme/korisnika u istom obliku (za poređenje pri izmjeni) */
const fieldText = (source, key) => (key === "paying" ? (typeof source.paying === "number" ? payingToForm(source.paying) : text(source.paying).trim()) : text(source[key]).trim());

/**
 * Tijelo POST/PATCH .../users-delivery: FLAT polja na vrhu (15.09.2026 odgovor backenda, potvrđeno 28.10.).
 * POST izostavlja prazne vrijednosti (prazan referral_short_url bi udario u UNIQUE). PATCH šalje samo promijenjene
 * vrijednosti, a polje koje je bilo popunjeno i sad je prazno šalje kao null (brisanje).
 * Lozinku postojećeg korisnika šalje samo administrator i samo ako je upisana; email se pri izmjeni nikad ne šalje.
 */
export const buildBody = (form, { isNew, isAdmin = false, original = null } = {}) => {
  const body = {
    name: form.name.trim(),
    lastname: form.lastname.trim(),
    type: form.type,
  };
  const phone = orNull(form.phone.trim());
  if (phone !== null || !isNew) {
    body.phone = phone;
  }
  if (isNew) {
    body.email = form.email.trim();
    body.password = form.password;
    body.state = 1;
    body.can_report = 0;
  } else if (isAdmin && form.password) {
    body.password = form.password;
  }
  if (form.type === COURIER_TYPE) {
    COURIER_ONLY.forEach((key) => {
      const raw = fieldText(form, key);
      if (!isNew && original && raw === fieldText(original, key)) {
        return;
      }
      if (raw === "") {
        if (!isNew) body[BODY_FIELDS[key]] = null;
        return;
      }
      if (key === "paying") body.paying = parseAmount(raw);
      else if (key === "ptype") body.paying_type = Number(raw);
      else if (key === "rby" && /^\d+$/.test(raw)) body.referred_by = Number(raw);
      else body[BODY_FIELDS[key]] = raw;
    });
  }
  return body;
};

/** 422 odgovor -> { fields: { formskiKljuč: poruka }, rest } (prva poruka po polju); `rest` je poruka bez polja. */
export const mapServerErrors = (response) => {
  const fields = {};
  let rest = "";
  const data = (response && response.data) || {};
  const reverse = Object.keys(BODY_FIELDS).reduce((acc, key) => ({ ...acc, [BODY_FIELDS[key]]: key }), {
    name: "name",
    lastname: "lastname",
    email: "email",
    phone: "phone",
    password: "password",
    type: "type",
  });
  Object.keys(data.errors || {}).forEach((field) => {
    const message = [].concat(data.errors[field])[0];
    if (reverse[field]) fields[reverse[field]] = message;
    else if (!rest) rest = message;
  });
  if (!Object.keys(fields).length && !rest) {
    rest = text(data.message);
  }
  return { fields, rest };
};

// ------------------------------------------------------------------ prikaz
export const payingText = (user, { currency = "KM", number = (n) => String(n) } = {}) => {
  if (!user.ptype || user.paying === null) {
    return "";
  }
  return user.ptype === 2 ? `${number(user.paying)} %` : `${number(user.paying)} ${currency}`;
};
